import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  activeProcessSF,
  getActiveProcessKey,
  handleProcess,
  IRydeProcess,
  RydeProcessTitle,
  RydeUiUnit,
  selectedTableRowsIdsAtomFamily,
  TAddressedFieldsValueOnCompleted,
  useProcesses,
} from "@ryde/shared";

import { useDynamicFormsListActions } from "../use-dynamic-forms-list-actions";

export const useWatchBulkRideArchive = ({
  entity,
  addressedFieldsValueOnCompleted,
}: {
  entity: RydeUiUnit;
  addressedFieldsValueOnCompleted: TAddressedFieldsValueOnCompleted;
}) => {
  const bulkArchiveRidesProcessKey = getActiveProcessKey(
    RydeProcessTitle.BulkArchive,
    entity,
  );
  const setSelectedTableRowsIdsAtomFamily = useSetRecoilState(
    selectedTableRowsIdsAtomFamily(entity),
  );
  const bulkArchiveRidesProcess = useRecoilValue(
    activeProcessSF(bulkArchiveRidesProcessKey),
  );
  const { removeRows } = useDynamicFormsListActions(entity);
  const { stopProcessPolling } = useProcesses();
  const { t } = useTranslation();

  const onComplete = useCallback(
    (process: IRydeProcess) => {
      const successIds = process.metadata?.metrics?.successIds ?? [];
      if (successIds.length) {
        removeRows(successIds);
      }
      addressedFieldsValueOnCompleted(process, false);

      setSelectedTableRowsIdsAtomFamily(prev => {
        if (!successIds.length) {
          return prev;
        }
        const successIdsSet = new Set(successIds);
        return prev.filter(id => !successIdsSet.has(id));
      });
    },
    [
      addressedFieldsValueOnCompleted,
      removeRows,
      setSelectedTableRowsIdsAtomFamily,
    ],
  );

  useEffect(() => {
    if (entity !== "ride") {
      return;
    }

    handleProcess({
      entity,
      processTitle: RydeProcessTitle.BulkArchive,
      process: bulkArchiveRidesProcess,
      onComplete,
      translationFunction: t,
      stopProcessPolling,
      toastSummaryOnComplete: true,
    });
  }, [bulkArchiveRidesProcess, entity, onComplete, stopProcessPolling, t]);
};
